import { Box, Stack, Typography } from "@mui/material";
import {
  useFollowedUsersQuery,
  useFollowersQuery,
} from "Api/Queries/Social/useFollowListsQuery";
import { useMutualFollowersQuery } from "Api/Queries/Social/useMutualFollowersQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import {
  QueryParamTabs,
  type TabItem,
} from "Components/Shared/Tabs/QueryParamTabs";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { BlockedUsersLink } from "Pages/People/BlockedUsersLink";
import { FollowersSection } from "Pages/People/FollowersSection";
import { FollowingSection } from "Pages/People/FollowingSection";
import { FollowRequestsSection } from "Pages/People/FollowRequestsSection";
import { MutualListSection } from "Pages/People/MutualListSection";
import { UserSearchOverlay } from "Pages/People/UserSearchOverlay";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { canRoleFollow } from "Utils/Social/socialRules";
import { Navigate } from "Navigation/Navigate";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

const RESOURCES = Resources.Social.People;

/**
 * The social hub: find people by handle or name, manage follow requests, mutual followers and
 * follows.
 *
 * Only Customer personas follow others (Social ADR-0002; canRoleFollow mirrors the backend
 * rule): business personas have their own page — FollowersPage — and one landing here (old link,
 * typed URL) is sent there.
 */
export const PeoplePage = () => {
  const { t } = useResource();
  const isSocialPersona = canRoleFollow(useCurrentUserAccessRole());

  // First pages of the three tab lists, fetched here for the tab-label totals —
  // when the active tab's section is on page 1, React Query dedupes its query
  // with these.
  const { data: mutuals } = useMutualFollowersQuery();
  const { data: followed } = useFollowedUsersQuery();
  const { data: followers } = useFollowersQuery();

  if (!isSocialPersona) {
    return <Navigate to={appUrl("followers")} replace />;
  }

  // One wrapper span, or the Tab's column-flex root stacks title and count on two
  // lines. The count keeps a fixed gray regardless of the tab's active color.
  const tabLabel = (key: string, count: number | undefined) => (
    <Box component="span" sx={{ textTransform: "none" }}>
      {t(key)}
      {!!count && (
        <Typography
          component="span"
          variant="inherit"
          color="text.disabled"
          ml={1.25}
        >
          {count}
        </Typography>
      )}
    </Box>
  );

  const tabs: Array<TabItem> = [
    {
      id: "mutual",
      label: tabLabel(RESOURCES.Tabs.Mutual, mutuals?.totalCount),
      content: (
        <Stack gap={2}>
          <FollowRequestsSection />
          <MutualListSection />
        </Stack>
      ),
    },
    {
      id: "following",
      label: tabLabel(RESOURCES.Tabs.Following, followed?.totalCount),
      content: <FollowingSection />,
    },
    {
      id: "followers",
      label: tabLabel(RESOURCES.Tabs.Followers, followers?.totalCount),
      content: <FollowersSection />,
    },
  ];

  return (
    <AuthenticatedLayout title={t(Resources.Pages.Titles.People)}>
      <StyledPageWrapper>
        <Stack className="left-content" gap={2} mb={6}>
          <UserSearchOverlay />
          <QueryParamTabs tabs={tabs} hideScrollButtons />
          <BlockedUsersLink />
        </Stack>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
